'use client'

import { useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { compact, uniq } from 'lodash'

import { getChain } from '~/constants/chains'
import { properlyFormatOneOrMoreChainNames } from '~/utils/chainLabels'
import { categorizeMissingChains } from '~/utils/walletConnectChains'

type ChainSupportParams = {
  requiredChainIds: number[]
  optionalChainIds: number[]
  supportedChainIds: number[]
}

export const useWalletConnectChainSupport = ({
  requiredChainIds,
  optionalChainIds,
  supportedChainIds,
}: ChainSupportParams) => {
  const queryClient = useQueryClient()

  return useMemo(() => {
    const allRequestedChainIds = uniq(requiredChainIds.concat(optionalChainIds))

    const filteredSupportedChainIds = allRequestedChainIds.filter((chainId) => {
      return supportedChainIds.includes(chainId)
    })

    const isRequired = requiredChainIds.length > 0
    const appSupportsCurrentChains = isRequired
      ? requiredChainIds.filter((id) => !filteredSupportedChainIds.includes(id))
          .length === 0
      : filteredSupportedChainIds.length > 0

    const chainsToShow = isRequired
      ? uniq(requiredChainIds)
      : uniq(optionalChainIds)

    // Categorize missing required chains
    const missingRequiredChainIds = requiredChainIds.filter(
      (id) => !filteredSupportedChainIds.includes(id),
    )
    const {
      unsupportedByTeamsChainIds,
      notEnabledOnOrgChainIds,
      unsupportedByTeamsChainNames,
      notEnabledOnOrgChainNames,
      hasUnsupportedByTeamsChains,
      hasNotEnabledOnOrgChains,
    } = categorizeMissingChains(missingRequiredChainIds, queryClient)

    const chainNames = compact(chainsToShow.map((id) => getChain(id))).map(
      (chain) => chain.name,
    )
    const formattedChainNames = properlyFormatOneOrMoreChainNames(chainNames)
    const isPlural = chainsToShow.length > 1

    const hasNoSupportedChains = chainNames.length === 0

    return {
      filteredSupportedChainIds,
      appSupportsCurrentChains,
      formattedChainNames,
      isPlural,
      hasNoSupportedChains,
      // New fields for granular messaging
      unsupportedByTeamsChainIds,
      notEnabledOnOrgChainIds,
      unsupportedByTeamsChainNames,
      notEnabledOnOrgChainNames,
      hasUnsupportedByTeamsChains,
      hasNotEnabledOnOrgChains,
    }
  }, [requiredChainIds, optionalChainIds, supportedChainIds, queryClient])
}
